import React, { Suspense, useCallback, useMemo, useState, useEffect } from "react";
import { Box, Card, CardContent, CircularProgress, Typography, IconButton, Tooltip } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { saveProfile, submitProfile, setSuccess, initInformation } from "../../store/setProfile";
import { selectIsDraftDirty } from "../../store/setProfile/selectors";
import SweetAlert from "sweetalert2";
// @ts-ignore - SVG import
import errorIcon from "../../assets/error-icon.svg";
import StepSidebar from "./components/StepSidebar";
import ActionBar from "./components/ActionBar";
import { useStepper } from "./hooks/useStepper";
import { stepsConfig } from "./step-config";
import Subjects from "./steps/Subjects";
import TutoringOptions from "./steps/TutoringOptions";
import TutorProfile from "./steps/TutorProfile";
import Qualifications from "./steps/Qualifications";
import ContactInfo from "./steps/ContactInfo";
import Subscription from "./steps/Subscription";
import Submit from "./steps/Submit";
import StepHeader from "./components/StepHeader";
import "./OnboardingLayout.css";

const stripePublicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
const stripePromise = loadStripe(stripePublicKey || "");

export default function OnboardingLayout() {
  const { step, steps, index, setStep, next, prev } = useStepper();
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const { information, success, message } = useSelector((state: any) => state.profile);
  const isDraftDirty = useSelector(selectIsDraftDirty);

  const StepComponent = useMemo(() => {
    switch (step.id) {
      case "subjects":
        return Subjects as any;
      case "tutoring-options":
        return TutoringOptions as any;
      case "tutor-profile":
        return TutorProfile as any;
      case "qualifications":
        return Qualifications as any;
      case "contact-info":
        return ContactInfo as any;
      case "subscription":
        return Subscription as any;
      case "submit":
        return Submit as any;
      default:
        return Subjects as any;
    }
  }, [step.id]);

  const handleSave = useCallback(async () => {
    // Call step's save method if it exists
    if ((StepComponent as any).save) {
      await (StepComponent as any).save();
    }

    // Only save to backend if there are changes
    if (!isDraftDirty && !isDirty) {
      next();
      return;
    }

    // Dispatch saveProfile action
    // @ts-ignore - Redux thunk action creator
    dispatch(saveProfile({ information, setLoading, nextStep: next }));
  }, [StepComponent, isDraftDirty, isDirty, information, dispatch, next]);

  const handleSubmit = useCallback(() => {
    // @ts-ignore - Redux thunk action creator
    dispatch(submitProfile({ information, setLoading, navigate }));
  }, [dispatch, information, navigate]);

  const handleDirtyChange = useCallback((dirty: boolean) => {
    setIsDirty(dirty);
  }, []);

  // Error handling with SweetAlert
  useEffect(() => {
    if (success > 0) {
      dispatch(setSuccess({ success: 0, message: "" }));
      return;
    }
    if (success < 0) {
      SweetAlert.fire({
        imageUrl: errorIcon,
        imageHeight: "100px",
        title: "Oops...",
        text:
          message && message.length > 0
            ? message
            : "Can't connect with server, Please try again",
        confirmButtonColor: "#712d94",
      });
      dispatch(setSuccess({ success: 0, message: "" }));
    }
  }, [success, message, dispatch]);

  // Initialize information on mount
  useEffect(() => {
    dispatch(setSuccess({ success: 0, message: "" }));
    return () => {
      dispatch(initInformation());
    };
  }, [dispatch]);

  const canNext = (StepComponent as any).valid ?? true;

  return (
    <Box className="onboarding-container">
      <Box className="onboarding-wrapper">
        <Box className="onboarding-header">
          <StepHeader activeIndex={index} />
        </Box>
        
        {/* Compact horizontal header bar */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 2.5,
            gap: 1.5,
            flexWrap: 'wrap'
          }}
        >
          <Tooltip title="Back To Dashboard" placement="bottom">
            <IconButton 
              href="/dashboard"
              sx={{ 
                color: "#712d94",
                "&:hover": {
                  backgroundColor: "rgba(113, 45, 148, 0.08)",
                },
              }}
            >
              <ArrowBack />
            </IconButton>
          </Tooltip>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                color: "#712d94",
                margin: 0,
                fontSize: '1.25rem',
              }}
            >
              {stepsConfig[index].title}
            </Typography>
            <Typography 
              sx={{ 
                color: '#6b7280', 
                fontSize: "0.875rem",
                fontWeight: 400,
              }}
            >
              · {stepsConfig[index].description}
            </Typography>
          </Box>
        </Box>

        <Box className="onboarding-main">
          <StepSidebar activeIndex={index} onSelect={setStep} />
          <Card className="onboarding-content-card">
            <CardContent className="onboarding-content">
              <Suspense fallback={<Box sx={{ p: 4, textAlign: "center" }}><CircularProgress /></Box>}>
                {step.id === "subscription" ? (
                  <Elements stripe={stripePromise}>
                    <StepComponent 
                      onDirtyChange={handleDirtyChange}
                      onSave={handleSave}
                      loading={loading}
                    />
                  </Elements>
                ) : step.id === "submit" ? (
                  <StepComponent 
                    onDirtyChange={handleDirtyChange}
                    onSave={handleSave}
                    onSubmit={handleSubmit}
                    onBack={prev}
                    loading={loading}
                  />
                ) : (
                  <StepComponent 
                    onDirtyChange={handleDirtyChange}
                    onSave={handleSave}
                    loading={loading}
                  />
                )}
              </Suspense>
            </CardContent>
            {step.id !== "submit" && (
              <ActionBar 
                onBack={prev} 
                onSave={handleSave} 
                onNext={() => { handleSave(); }} 
                disableNext={!canNext || loading} 
              />
            )}
          </Card>
        </Box>
      </Box>
    </Box>
  );
}


